// PhotoSwipe lightbox for Gutenberg gallery images (.gutenberg-content
// .wp-block-gallery). Seeded gallery images aren't wrapped in links, so the
// data source is built from the rendered <img> tags: full-size URL = largest
// srcset candidate, dimensions from the width/height attributes. Core is
// imported statically — the bundle loads as a classic script, where Vite's
// dynamic-import helper (import.meta) would throw.
// PhotoSwipe CSS is inlined into theme-styles.scss (the enqueued stylesheet).
import PhotoSwipeLightbox from "photoswipe/lightbox";
import PhotoSwipe from "photoswipe";
import type { SlideData } from "photoswipe";

function largestSrcsetCandidate(srcset: string): string | null {
  let bestUrl: string | null = null;
  let bestW = 0;
  for (const candidate of srcset.split(",")) {
    const [url, descriptor] = candidate.trim().split(/\s+/);
    const w = parseFloat(descriptor) || 0;
    if (url && w >= bestW) {
      bestW = w;
      bestUrl = url;
    }
  }
  return bestUrl;
}

type GallerySlide = SlideData & { caption?: string };

function slideFromImg(img: HTMLImageElement): GallerySlide {
  return {
    src: (img.srcset && largestSrcsetCandidate(img.srcset)) || img.src,
    srcset: img.srcset || undefined,
    // msrc: the already-loaded thumbnail doubles as the loading placeholder —
    // without it PhotoSwipe shows a dark box until the full image arrives.
    msrc: img.currentSrc || img.src,
    width: parseInt(img.getAttribute("width") || "", 10) || img.naturalWidth,
    height: parseInt(img.getAttribute("height") || "", 10) || img.naturalHeight,
    alt: img.alt,
    caption: img.closest("figure")?.querySelector("figcaption")?.textContent?.trim(),
  };
}

function setupGallery(gallery: HTMLElement): void {
  const imgs = [...gallery.querySelectorAll<HTMLImageElement>("figure.wp-block-image img")];
  if (!imgs.length) return;

  const lightbox = new PhotoSwipeLightbox({
    dataSource: imgs.map(slideFromImg),
    pswpModule: PhotoSwipe,
  });
  (gallery as unknown as { __pswpLightbox?: PhotoSwipeLightbox }).__pswpLightbox = lightbox;

  // Figcaption text rendered center-bottom (styled in theme-styles.scss).
  lightbox.on("uiRegister", () => {
    lightbox.pswp?.ui?.registerElement({
      name: "custom-caption",
      appendTo: "root",
      onInit: (el, pswp) => {
        const update = (): void => {
          el.textContent = (pswp.currSlide?.data as GallerySlide)?.caption ?? "";
        };
        pswp.on("change", update);
        update();
      },
    });
  });

  lightbox.init();

  const clickHandlers: Array<[HTMLImageElement, () => void]> = imgs.map((img, index) => [
    img,
    () => lightbox.loadAndOpen(index),
  ]);
  clickHandlers.forEach(([img, handler]) => {
    img.style.cursor = "zoom-in";
    img.addEventListener("click", handler);
  });
  (gallery as unknown as { __pswpClickHandlers?: typeof clickHandlers }).__pswpClickHandlers =
    clickHandlers;
}

const initialized = new WeakSet<HTMLElement>();

function initAll(): void {
  document
    .querySelectorAll<HTMLElement>(".gutenberg-content .wp-block-gallery")
    .forEach((gallery) => {
      if (initialized.has(gallery)) return;
      initialized.add(gallery);
      setupGallery(gallery);
    });
}

// Destroy lightbox instances + drop the guard before htmx morphs the DOM.
// idiomorph can hand the same gallery node to a structurally-identical
// destination gallery; without this the stale instance keeps a dataSource
// built from the old page's <img> tags, and the reused node never re-inits.
function destroyAll(): void {
  document
    .querySelectorAll<HTMLElement>(".gutenberg-content .wp-block-gallery")
    .forEach((gallery) => {
      const g = gallery as unknown as {
        __pswpLightbox?: PhotoSwipeLightbox;
        __pswpClickHandlers?: Array<[HTMLImageElement, () => void]>;
      };
      g.__pswpLightbox?.destroy();
      delete g.__pswpLightbox;
      g.__pswpClickHandlers?.forEach(([img, handler]) =>
        img.removeEventListener("click", handler),
      );
      delete g.__pswpClickHandlers;
      initialized.delete(gallery);
    });
}

document.addEventListener("DOMContentLoaded", initAll);
// htmx boost swaps content without a full reload — bind any new galleries.
document.addEventListener("htmx:load", initAll);
document.addEventListener("htmx:before-swap", destroyAll);
